import { Component, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ApiCore } from 'src/app/config/api';
import { ApiService } from 'src/app/core/http/api.service';
import { ToastService } from 'src/app/shared/services/toast.service';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.css'],
})
export class ChangePasswordComponent implements OnInit {
  svgIcon: any;
  userProfile: any = [];
  public updatingPassword = false;
  showCurrentPassword: boolean = false;
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;

  currentPasswordError: string = '';
  newPasswordError: string = '';
  confirmPasswordError: string = '';

  showConfirmDialog: boolean = false;
  processing: boolean = false;

  public logo = 'assets/images/no-photo-available.png';
  public passwordChangeForm: FormGroup = this.formBuilder.group({
    currentPassword: ['', [Validators.required, Validators.minLength(8)]],
    newPassword: ['', [Validators.required, this.passwordStrengthValidator]],
    confirmPassword: ['', [Validators.required]],
  });

  constructor(
    private formBuilder: FormBuilder,
    private sanitizer: DomSanitizer,
    private toast: ToastService,
    private api: ApiService
  ) {}

  ngOnInit(): void {
    this.LoadUserData();
    this.getCoorporateConfiguration();

    this.svgIcon = this.sanitizer.bypassSecurityTrustHtml(
      '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0fb981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-lock-password absolute bottom-0" style="margin-bottom: -3px; margin-left: 10px"><path stroke="none" d="M0 0h24v24H0z" fill="none"></path><path d="M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2z"></path><path d="M8 11v-4a4 4 0 1 1 8 0v4"></path><path d="M15 16h.01"></path><path d="M12.01 16h.01"></path><path d="M9.02 16h.01"></path></svg>'
    );

    this.passwordChangeForm.valueChanges.subscribe(() => {
      this.passwordsMatchValidator(this.passwordChangeForm);
    });

    this.passwordChangeForm
      .get('currentPassword')
      ?.valueChanges.subscribe((val) => {
        const cleaned = val.replace(/\s/g, '');
        if (val !== cleaned) {
          this.passwordChangeForm
            .get('currentPassword')
            ?.setValue(cleaned, { emitEvent: false });
        }
      });
    this.passwordChangeForm
      .get('newPassword')
      ?.valueChanges.subscribe((val) => {
        const cleaned = val.replace(/\s/g, '');
        if (val !== cleaned) {
          this.passwordChangeForm
            .get('newPassword')
            ?.setValue(cleaned, { emitEvent: false });
        }
      });
    this.passwordChangeForm
      .get('confirmPassword')
      ?.valueChanges.subscribe((val) => {
        const cleaned = val.replace(/\s/g, '');
        if (val !== cleaned) {
          this.passwordChangeForm
            .get('confirmPassword')
            ?.setValue(cleaned, { emitEvent: false });
        }
      });
  }

  LoadUserData() {
    this.api.list(ApiCore.USER + '/me').subscribe(
      (res) => {
        this.userProfile = res.data;
      },
      (error) => {
        this.toast.showError('Error al cargar los datos del usuario', '');
      }
    );
  }

  private getCoorporateConfiguration(): void {
    this.api.getCoorporateCasinoConfig().subscribe({
      next: (res) => {
        this.logo = res.data.logoCasinoUrl;
      },
      error: (err) => console.error(err),
    });
  }

  validateCurrentPassword(evt: any) {
    const value: string = evt.target.value;
    this.currentPasswordError = '';

    if (value.length < 8 && value.length > 0) {
      this.currentPasswordError =
        'La contraseña debe tener al menos 8 caracteres';
    }
  }

  validateNewPassword(evt: any) {
    const value: string = evt.target.value;
    this.newPasswordError = '';

    if (value.length < 8 && value.length > 0) {
      this.newPasswordError = 'Utiliza al menos 8 caracteres';
    } else if (!/[0-9]/.test(value)) {
      this.newPasswordError = 'Utiliza al menos 1 número';
    } else if (!/[!@#$%^&*(),.?":{}|<>[\]\\/;:_+`~=-]/.test(value)) {
      this.newPasswordError = 'Utiliza al menos 1 caracter especial';
    } else if (!/[A-Z]/.test(value)) {
      this.newPasswordError = 'Utiliza al menos una letra mayúscula';
    } else if (!/[a-z]/.test(value)) {
      this.newPasswordError = 'Utiliza al menos una letra minúscula';
    }

    this.validatePasswordMatch(
      value,
      this.passwordChangeForm.get('confirmPassword')?.value
    );
  }

  validateConfirmPassword(evt: any) {
    const value: string = evt.target.value;
    this.confirmPasswordError = '';

    this.validatePasswordMatch(
      this.passwordChangeForm.get('newPassword')?.value,
      value
    );
  }

  private validatePasswordMatch(newPassword: string, confirmPassword: string) {
    const match = newPassword === confirmPassword;

    this.confirmPasswordError = '';
    this.passwordChangeForm.get('confirmPassword')?.setErrors(null);

    if (!match) {
      this.confirmPasswordError = 'Contraseña no es igual';
      this.passwordChangeForm
        .get('confirmPassword')
        ?.setErrors({ invalid: true });
    }
  }

  validatePassword() {
    const password = this.passwordChangeForm.get('currentPassword')?.value;
    const email = this.userProfile.email;

    this.api
      .create('user/validate-current-password', {
        email,
        password,
      })
      .subscribe({
        next: (result) => {
          if (result.data.passwordMatches) {
            this.passwordChangeForm.get('currentPassword')?.setErrors(null);
            this.showConfirmDialog = true;
          } else {
            this.passwordChangeForm
              .get('currentPassword')
              ?.setErrors({ incorrect: true });
            this.toast.showError(
              'Credenciales inválidas para hacer cambio',
              ''
            );
          }
        },
      });
  }

  confirmChangePassword() {
    this.updatingPassword = true;
    this.processing = true;
    

    this.api
      .updateDirectPost('auth/change-password', {
        oldPassword: this.passwordChangeForm.get('currentPassword')?.value,
        newPassword: this.passwordChangeForm.get('newPassword')?.value,
      })
      .subscribe({
        next: (val)=>{
          this.toast.showSuccess(
            'Contraseña actualizada satisfactoriamente',
            ''
          );
          this.processing = false;
          this.showConfirmDialog = false;
          this.passwordChangeForm.reset();
        },
        error: (err)=>{
          this.toast.showError('Error al actualizar tu contraseña', '');
          this.processing = false;
          this.showConfirmDialog = false;
        },
        complete: ()=>{
          this.updatingPassword = false;
        }
      });
  }

  passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value || '';

    if (value.length < 8) {
      return { passwordStrength: 'Utiliza al menos 8 caracteres' };
    }
    if (!/[0-9]/.test(value)) {
      return { passwordStrength: 'Utiliza al menos 1 número' };
    }
    if (!/[!@#$%^&*(),.?":{}|<>[\]\\/;:_+`~=-]/.test(value)) {
      return { passwordStrength: 'Utiliza al menos 1 caracter especial' };
    }
    if (!/[A-Z]/.test(value)) {
      return { passwordStrength: 'Utiliza al menos una letra mayúscula' };
    }
    if (!/[a-z]/.test(value)) {
      return { passwordStrength: 'Utiliza al menos una letra minúscula' };
    }

    return null;
  }

  passwordsMatchValidator(formGroup: FormGroup) {
    const newPassword = formGroup.get('newPassword')?.value;
    const confirmPassword = formGroup.get('confirmPassword')?.value;

    if (newPassword !== confirmPassword) {
      formGroup.get('confirmPassword')?.setErrors({ notMatching: true });
    } else {
      // Si no hay otros errores, eliminamos el de coincidencia
      const errors = formGroup.get('confirmPassword')?.errors;
      if (errors) {
        delete errors['notMatching'];
        if (Object.keys(errors).length === 0) {
          formGroup.get('confirmPassword')?.setErrors(null);
        } else {
          formGroup.get('confirmPassword')?.setErrors(errors);
        }
      }
    }
  }
  toggleCurrentPasswordVisibility() {
    this.showCurrentPassword = !this.showCurrentPassword;
  }
  toggleNewPasswordVisibility() {
    this.showNewPassword = !this.showNewPassword;
  }
  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }
  closeConfirmDialog() {
    this.showConfirmDialog = false;
  }

  closeOnBackdrop(event: MouseEvent) {
    // Si el click es en el backdrop (fuera del contenido), cerrar modal
    this.showConfirmDialog = false;
  }
}
